import React, { useState } from 'react';
import { Box, Button, Chip, Collapse, LinearProgress, Paper, Tooltip, Typography } from '@mui/material';
import { CheckCircle, Cancel, RemoveCircleOutline, ExpandMore, ExpandLess } from '@mui/icons-material';

// Profil-teljesség megjelenítése (backend: utils/providerCompleteness.js)

// Melyik profilszerkesztő fülön pótolható az adott tétel (ProviderEditPage ?tab=)
export const CHECK_TAB = { profileImage: 2, gallery: 2, description: 0, category: 0, city: 0, contact: 0, price: 3 };

export function ProviderCompletenessChip({ completeness }) {
  if (!completeness) return null;
  const { ready, requiredMet, requiredTotal, missing } = completeness;
  const tooltip = missing.length ? `Hiányzik: ${missing.join(', ')}` : 'Minden szempont teljesül';
  return (
    <Tooltip title={tooltip}>
      <Chip
        size="small"
        color={ready ? 'success' : 'warning'}
        variant={ready ? 'filled' : 'outlined'}
        label={ready ? `Kész (${requiredMet}/${requiredTotal})` : `Hiányos (${requiredMet}/${requiredTotal})`}
      />
    </Tooltip>
  );
}

// Kompakt sáv a profilszerkesztő tetejére: haladás + a hiányzó tételek chipként, a teljes
// lista lenyitható. Szolgáltatónak és adminnak ugyanaz.
export function ProviderCompletenessBar({ completeness, onCheckClick }) {
  const [open, setOpen] = useState(false);
  if (!completeness) return null;
  const { ready, requiredMet, requiredTotal, checks } = completeness;
  const missingRequired = checks.filter(c => c.required && !c.ok);
  return (
    <Paper variant="outlined" sx={{ p: 1.5, mb: 2, borderColor: ready ? 'success.light' : 'warning.light' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
        <Typography variant="body2" fontWeight={600} sx={{ whiteSpace: 'nowrap' }}>
          {ready ? 'A profil kész' : `Profil kitöltöttsége: ${requiredMet}/${requiredTotal}`}
        </Typography>
        <LinearProgress
          variant="determinate"
          value={(requiredMet / requiredTotal) * 100}
          color={ready ? 'success' : 'warning'}
          sx={{ flex: '1 1 120px', height: 6, borderRadius: 3, minWidth: 120 }}
        />
        <Button size="small" onClick={() => setOpen(o => !o)} endIcon={open ? <ExpandLess /> : <ExpandMore />}>
          Részletek
        </Button>
      </Box>
      {missingRequired.length > 0 && (
        <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mt: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ alignSelf: 'center' }}>Még hiányzik:</Typography>
          {missingRequired.map(c => (
            <Chip
              key={c.key}
              size="small"
              variant="outlined"
              color="warning"
              label={c.label}
              // Kattintásra a tételt pótló fülre / mezőre visz
              {...(onCheckClick ? { onClick: () => onCheckClick(c), clickable: true } : {})}
            />
          ))}
        </Box>
      )}
      <Collapse in={open}>
        <Box sx={{ mt: 1 }}>
          <ProviderCompletenessChecklist completeness={completeness} />
        </Box>
      </Collapse>
    </Paper>
  );
}

export function ProviderCompletenessChecklist({ completeness }) {
  if (!completeness) return null;
  return (
    <Box component="ul" sx={{ listStyle: 'none', p: 0, m: 0 }}>
      {completeness.checks.map((check) => {
        let icon = <CheckCircle fontSize="small" color="success" />;
        if (!check.ok) {
          icon = check.required
            ? <Cancel fontSize="small" color="error" />
            : <RemoveCircleOutline fontSize="small" color="disabled" />;
        }
        return (
          <Box component="li" key={check.key} sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 0.25 }}>
            {icon}
            <Typography variant="body2">
              {check.label}
              {!check.required && ' (ajánlott)'}
              {check.detail && (
                <Typography component="span" variant="body2" color="text.secondary"> — {check.detail}</Typography>
              )}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
}
